import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Ticket, 
  Plus, 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  MapPin,
  Phone,
  Package,
  Send,
  Eye,
  Edit,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Wrench,
  AlertTriangle,
  FileText,
  List,
  Grid,
  MessageCircle,
  CheckCheck,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Tickets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedBranch } = useApp();
  const userBranchId = user?.branch_id ? String(user.branch_id) : null;
  const isBranchScoped = ['kepala_cabang', 'teknisi', 'customer service'].includes(String(user?.role || '').toLowerCase()) && userBranchId;
  
  // Check if user is owner or kepala_cabang for branch filtering
  const isOwnerOrKepala = user?.role === 'owner' || user?.role === 'kepala_cabang';
  
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  
  // Form states for create ticket
  const [customers, setCustomers] = useState([]);
  // 2026-06-04: lock the customer dropdown when navigated from Customer Detail
  // → Tickets via prefilledCustomer state. UX: tidak bisa diubah tanpa sengaja.
  const [prefilledCustomerLock, setPrefilledCustomerLock] = useState(null);
  // 2026-06-05: searchable customer picker (replaces native dropdown).
  // Cari ke server (live search) — bisa cari customer di luar limit=500.
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [selectedCustomerObj, setSelectedCustomerObj] = useState(null);
  const [teknisi, setTeknisi] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    ticket_type: 'PROBLEM',
    priority: 'NORMAL',
    notes: '',
    assigned_to: '',
    assigned_to_2: ''
  });
  
  // Edit and Delete modal states
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    assigned_to_2: '',
    notes: ''
  });

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchCustomers();
    fetchTeknisi();
    fetchBranches();
    
    if (location.state?.prefilledCustomer) {
      const customer = location.state.prefilledCustomer;
      // 2026-06-05: searchable picker — set selectedCustomerObj langsung.
      setSelectedCustomerObj(customer);
      setFormData(prev => ({
        ...prev,
        customer_id: customer.id
      }));
      setPrefilledCustomerLock(customer);
      setShowCreateModal(true);
    }
  }, [filterStatus, filterType, selectedBranch]);

  // 2026-06-05: debounced live search to /api/v2/customers?search=...
  useEffect(() => {
    const term = (customerSearchTerm || '').trim();
    if (term.length < 2) {
      setCustomerSearchResults([]);
      setCustomerSearchLoading(false);
      return;
    }
    setCustomerSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        let url = `${BACKEND_URL}/api/v2/customers?limit=20&search=${encodeURIComponent(term)}`;
        if (selectedBranch && selectedBranch !== 'all') {
          url += `&branch_id=${selectedBranch}`;
        }
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setCustomerSearchResults(res.data?.data || []);
      } catch (e) {
        console.error('Customer search failed:', e);
        setCustomerSearchResults([]);
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [customerSearchTerm, selectedBranch]);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('ticket_type', filterType);
      
      // Apply branch filter from global context
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTickets(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/tickets/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use V2 endpoint which has branch filtering built-in
      let url = `${BACKEND_URL}/api/v2/customers?limit=500`;
      
      // Apply branch filter from global context for admin
      if (selectedBranch && selectedBranch !== 'all') {
        url += `&branch_id=${selectedBranch}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchTeknisi = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const users = response.data.data || [];
      setTeknisi(users.filter(u => u.role === 'teknisi'));
    } catch (error) {
      console.error('Error fetching teknisi:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/tickets`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowCreateModal(false);
      fetchTickets();
      fetchStats();
      
      setPrefilledCustomerLock(null);
      setSelectedCustomerObj(null);
      setCustomerSearchTerm('');
      setCustomerSearchResults([]);
      setCustomerSearchOpen(false);
      setFormData({
        customer_id: '',
        ticket_type: 'PROBLEM',
        priority: 'NORMAL',
        notes: '',
        assigned_to: '',
        assigned_to_2: ''
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Gagal membuat tiket: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'CLOSE': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'text-red-600';
      case 'HIGH': return 'text-orange-600';
      case 'NORMAL': return 'text-blue-600';
      case 'LOW': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'PEMASANGAN': return <Wrench className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      case 'PROBLEM': return <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />;
      case 'REQUEST': return <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
      default: return <Ticket className="h-4 w-4 text-gray-600 dark:text-slate-300" />;
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedTickets = React.useMemo(() => {
    let sortableTickets = [...tickets];
    if (sortConfig.key) {
      sortableTickets.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableTickets;
  }, [tickets, sortConfig]);

  const filteredTickets = sortedTickets.filter(ticket => {
    const searchLower = searchTerm.toLowerCase();
    return (
      ticket.ticket_number?.toLowerCase().includes(searchLower) ||
      ticket.customer_name?.toLowerCase().includes(searchLower) ||
      ticket.assigned_to_name?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredTickets.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredTickets.length / entriesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(Number(value));
    setCurrentPage(1);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronDown className="h-3 w-3 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="h-3 w-3 text-indigo-600 dark:text-indigo-400" /> : 
      <ChevronDown className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />;
  };

  // Handle Edit Ticket
  const handleEditTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/tickets/${selectedTicket.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditModal(false);
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Gagal mengupdate tiket');
    }
  };

  // Handle Delete Ticket
  const handleDeleteTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/tickets/${selectedTicket.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteModal(false);
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert('Gagal menghapus tiket');
    }
  };

  // Open Edit Modal
  const openEditModal = (ticket) => {
    setSelectedTicket(ticket);
    setEditFormData({
      status: ticket.status || '',
      priority: ticket.priority || '',
      assigned_to: ticket.assigned_to || '',
      assigned_to_2: ticket.assigned_to_2 || '',
      notes: ticket.notes || ''
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300">Memuat tiket...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-1 dark:text-slate-100">Kelola Tiket</h1>
        <p className="text-sm text-gray-600 dark:text-slate-300">Kelola tiket pemasangan dan problem</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow dark:bg-slate-800 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Total Tiket</p>
              <p className="text-xl font-bold text-gray-900 dark:text-slate-100">{stats?.total || 0}</p>
            </div>
            <div className="p-2 bg-indigo-100 rounded-lg dark:bg-indigo-900/40">
              <Ticket className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-blue-200 p-3 shadow-sm hover:shadow-md transition-shadow dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Open</p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{stats?.by_status?.OPEN || 0}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg dark:bg-blue-900/40">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-indigo-200 p-3 shadow-sm hover:shadow-md transition-shadow dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">In Progress</p>
              <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{stats?.by_status?.IN_PROGRESS || 0}</p>
            </div>
            <div className="p-2 bg-indigo-100 rounded-lg dark:bg-indigo-900/40">
              <Wrench className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-yellow-200 p-3 shadow-sm hover:shadow-md transition-shadow dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Pending</p>
              <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{stats?.by_status?.PENDING || 0}</p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg dark:bg-yellow-900/40">
              <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-green-200 p-3 shadow-sm hover:shadow-md transition-shadow dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Completed</p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">{stats?.by_status?.CLOSE || 0}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg dark:bg-green-900/40">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-3 shadow-sm hover:shadow-md transition-shadow dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Cancelled</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">{stats?.by_status?.CANCELLED || 0}</p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg dark:bg-red-900/40">
              <X className="h-4 w-4 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4 shadow-sm dark:bg-slate-800 dark:border-slate-700">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Cari tiket, customer, teknisi..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
              />
            </div>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
          >
            <option value="">Semua Status</option>
            <option value="OPEN">Open</option>
            <option value="PENDING">Pending</option>
            <option value="CLOSE">Close</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
          >
            <option value="">Semua Type</option>
            <option value="PEMASANGAN">Pemasangan</option>
            <option value="PROBLEM">Problem</option>
            <option value="REQUEST">Request</option>
          </select>

          <select
            value={filterBranch}
            onChange={(e) => { if (isBranchScoped) return; setFilterBranch(e.target.value); }}
            disabled={isBranchScoped}
            title={isBranchScoped ? 'Anda hanya dapat melihat tiket cabang Anda sendiri' : undefined}
            className={"px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent " + (isBranchScoped ? "bg-slate-100 cursor-not-allowed" : "")}
          >
            {!isBranchScoped && <option value="">Semua Cabang</option>}
            {branches.filter(b => !isBranchScoped || String(b.id) === userBranchId).map(branch => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </select>

          <div className="flex border border-gray-300 rounded-lg overflow-hidden dark:border-slate-600">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 ${viewMode === 'table' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-2 ${viewMode === 'card' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
          </div>

          <button
            onClick={() => navigate('/app/tickets/reports')}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
          >
            <BarChart3 className="h-4 w-4" />
            Laporan
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            Buat Tiket
          </button>
        </div>
      </div>

      {/* Entries per page and info */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600 dark:text-slate-300">Show</span>
          <select
            value={entriesPerPage}
            onChange={(e) => handleEntriesChange(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <span className="text-xs text-gray-600 dark:text-slate-300">entries</span>
        </div>
        <div className="text-xs text-gray-600 dark:text-slate-300">
          Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredTickets.length)} of {filteredTickets.length} entries
        </div>
      </div>

      {/* Tickets Display */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
                <tr>
                  <th 
                    className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
                    onClick={() => handleSort('ticket_number')}
                  >
                    <div className="flex items-center gap-1">
                      No. Tiket
                      <SortIcon columnKey="ticket_number" />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider dark:text-slate-200">
                    Type
                  </th>
                  <th 
                    className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
                    onClick={() => handleSort('customer_name')}
                  >
                    <div className="flex items-center gap-1">
                      Customer
                      <SortIcon columnKey="customer_name" />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider dark:text-slate-200">
                    Teknisi
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider dark:text-slate-200">
                    Status
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider dark:text-slate-200">
                    WA
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider dark:text-slate-200">
                    Priority
                  </th>
                  <th 
                    className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Dibuat
                      <SortIcon columnKey="created_at" />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 uppercase tracking-wider dark:text-slate-200">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-slate-800 dark:divide-slate-700">
                {currentEntries.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500 dark:text-slate-400">
                      <Ticket className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm">Tidak ada tiket ditemukan</p>
                    </td>
                  </tr>
                ) : (
                  currentEntries.map((ticket) => (
                    <tr 
                      key={ticket.id} 
                      className="hover:bg-gray-50 transition-colors dark:hover:bg-slate-700/50"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-medium text-gray-900 dark:text-slate-100">
                          #{ticket.ticket_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(ticket.ticket_type)}
                          <span className="text-xs text-gray-900 dark:text-slate-100">{ticket.ticket_type}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-medium text-gray-900 dark:text-slate-100">{ticket.customer_name}</div>
                        <div className="text-xs text-gray-500 dark:text-slate-400">{ticket.customer_phone}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-xs text-gray-900 dark:text-slate-100">{ticket.assigned_to_name || '-'}{ticket.assigned_to_2_name ? ` + ${ticket.assigned_to_2_name}` : ''}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center px-3 py-1 min-w-[80px] text-[10px] leading-4 font-semibold rounded-full border ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {ticket.wa_sent ? (
                          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400" title={`Terkirim: ${ticket.wa_sent_at ? new Date(ticket.wa_sent_at).toLocaleString('id-ID') : ''}`}>
                            <CheckCheck className="h-4 w-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-400" title="Belum terkirim">
                            <MessageCircle className="h-4 w-4" />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-slate-400">
                        {new Date(ticket.created_at).toLocaleDateString('id-ID', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/app/tickets/${ticket.id}`);
                            }}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                            title="Lihat Detail"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(ticket);
                            }}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors dark:text-amber-400 dark:hover:bg-amber-900/30"
                            title="Edit Tiket"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTicket(ticket);
                              setShowDeleteModal(true);
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors dark:text-red-400 dark:hover:bg-red-900/30"
                            title="Hapus Tiket"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {currentEntries.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Ticket className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-slate-400">Tidak ada tiket ditemukan</p>
            </div>
          ) : (
            currentEntries.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-shadow dark:bg-slate-800 dark:border-slate-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(ticket.ticket_type)}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">{ticket.ticket_type}</h3>
                      <p className="text-xs text-gray-500 dark:text-slate-400">#{ticket.ticket_number}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center justify-center px-3 py-1 min-w-[80px] text-[10px] font-semibold rounded-full border ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                </div>

                <div className="space-y-1 mb-3">
                  <div className="flex items-center gap-2 text-xs">
                    <User className="h-3 w-3 text-gray-400" />
                    <span className="text-gray-900 font-medium dark:text-slate-100">{ticket.customer_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Phone className="h-3 w-3 text-gray-400" />
                    <span className="text-gray-600 dark:text-slate-300">{ticket.customer_phone}</span>
                  </div>
                  {ticket.assigned_to_name && (
                    <div className="flex items-center gap-2 text-xs">
                      <User className="h-3 w-3 text-gray-400" />
                      <span className="text-gray-600 dark:text-slate-300">Teknisi: {ticket.assigned_to_name}</span>
                    </div>
                  )}
                  {ticket.assigned_to_2_name && (
                    <div className="flex items-center gap-2 text-xs">
                      <User className="h-3 w-3 text-gray-400" />
                      <span className="text-gray-600 dark:text-slate-300">Teknisi 2: {ticket.assigned_to_2_name}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs pt-3 border-t border-gray-100 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                    {/* WA Status indicator */}
                    {ticket.wa_sent ? (
                      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400" title="WA Terkirim">
                        <CheckCheck className="h-3 w-3" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-400" title="WA Belum Terkirim">
                        <MessageCircle className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                  <span className="text-gray-500 dark:text-slate-400">
                    {new Date(ticket.created_at).toLocaleDateString('id-ID')}
                  </span>
                </div>
                
                {/* Action buttons for card view */}
                <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                  <button
                    onClick={() => navigate(`/app/tickets/${ticket.id}`)}
                    className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                    title="Lihat Detail"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openEditModal(ticket)}
                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors dark:text-amber-400 dark:hover:bg-amber-900/30"
                    title="Edit Tiket"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setShowDeleteModal(true);
                    }}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors dark:text-red-400 dark:hover:bg-red-900/30"
                    title="Hapus Tiket"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-xs border rounded-lg ${
                    currentPage === pageNum
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:hover:bg-slate-700/50"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="p-5 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Buat Tiket Baru</h2>
                <button
                  onClick={() => {
                    setPrefilledCustomerLock(null);
                    setSelectedCustomerObj(null);
                    setCustomerSearchTerm('');
                    setCustomerSearchResults([]);
                    setCustomerSearchOpen(false);
                    setShowCreateModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateTicket} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Customer *
                </label>
                {selectedCustomerObj && !customerSearchOpen ? (
                  // Selected state — chip + ganti button
                  <div className="flex items-center justify-between gap-3 p-3 border-2 border-indigo-200 rounded-lg bg-indigo-50 dark:bg-indigo-900/30">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-indigo-900 truncate" data-testid="selected-customer-name">
                        {selectedCustomerObj.name}
                      </div>
                      <div className="text-xs text-indigo-600 truncate dark:text-indigo-400">
                        {[selectedCustomerObj.phone, selectedCustomerObj.pppoe_username, selectedCustomerObj.account_number]
                          .filter(Boolean).join(' • ')}
                      </div>
                    </div>
                    {!prefilledCustomerLock && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCustomerObj(null);
                          setFormData(prev => ({ ...prev, customer_id: '' }));
                          setCustomerSearchTerm('');
                          setCustomerSearchResults([]);
                          setCustomerSearchOpen(true);
                        }}
                        data-testid="change-customer-btn"
                        className="flex-shrink-0 px-3 py-1 text-xs border border-indigo-300 text-indigo-700 rounded hover:bg-white dark:text-indigo-300"
                      >
                        Ganti
                      </button>
                    )}
                  </div>
                ) : (
                  // Search input + live results
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        autoFocus={!selectedCustomerObj}
                        value={customerSearchTerm}
                        onChange={(e) => {
                          setCustomerSearchTerm(e.target.value);
                          setCustomerSearchOpen(true);
                        }}
                        onFocus={() => setCustomerSearchOpen(true)}
                        placeholder="Cari nama / no.HP / PPPoE / no.akun…"
                        data-testid="create-ticket-customer-search"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                      />
                    </div>
                    {customerSearchOpen && (
                      <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-slate-800 dark:border-slate-700">
                        {customerSearchLoading ? (
                          <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-slate-400">Mencari…</div>
                        ) : customerSearchResults.length === 0 ? (
                          <div className="px-3 py-4 text-center text-xs text-gray-400">
                            {customerSearchTerm.trim().length < 2
                              ? 'Ketik minimal 2 karakter untuk mencari…'
                              : 'Tidak ada customer cocok'}
                          </div>
                        ) : (
                          customerSearchResults.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setSelectedCustomerObj(c);
                                setFormData(prev => ({ ...prev, customer_id: c.id }));
                                setCustomerSearchOpen(false);
                                setCustomerSearchTerm('');
                                setCustomerSearchResults([]);
                              }}
                              data-testid={`search-result-customer-${c.id}`}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-0 dark:hover:bg-indigo-900/30 dark:border-slate-700"
                            >
                              <div className="font-medium text-gray-900 truncate dark:text-slate-100">{c.name}</div>
                              <div className="text-xs text-gray-500 truncate dark:text-slate-400">
                                {[c.phone, c.pppoe_username, c.account_number].filter(Boolean).join(' • ')}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* Hidden field untuk required validation HTML */}
                <input type="hidden" name="customer_id" value={formData.customer_id || ''} required />
                {prefilledCustomerLock && (
                  <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                    Customer telah dipilih otomatis dari halaman detail.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Tipe Tiket *
                  </label>
                  <select
                    required
                    value={formData.ticket_type}
                    onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                  >
                    <option value="PEMASANGAN">Pemasangan</option>
                    <option value="PROBLEM">Problem</option>
                    <option value="REQUEST">Request</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Prioritas *
                  </label>
                  <select
                    required
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                  >
                    <option value="LOW">Low</option>
                    <option value="NORMAL">Normal</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Assign ke Teknisi (Opsional)
                </label>
                <select
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                >
                  <option value="">Auto Assign</option>
                  {teknisi.map((tek) => (
                    <option key={tek.id} value={tek.id}>
                      {tek.full_name || tek.name || 'Teknisi'} - {tek.phone || 'No Phone'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Teknisi ke-2 (Opsional)
                </label>
                <select
                  value={formData.assigned_to_2}
                  onChange={(e) => setFormData({ ...formData, assigned_to_2: e.target.value })}
                  data-testid="assign-teknisi-2-select"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                >
                  <option value="">Tidak ada</option>
                  {teknisi.filter((tek) => String(tek.id) !== String(formData.assigned_to)).map((tek) => (
                    <option key={tek.id} value={tek.id}>
                      {tek.full_name || tek.name || 'Teknisi'} - {tek.phone || 'No Phone'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Catatan (Opsional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                  placeholder="Tambahkan catatan jika diperlukan..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setPrefilledCustomerLock(null);
                    setSelectedCustomerObj(null);
                    setCustomerSearchTerm('');
                    setCustomerSearchResults([]);
                    setCustomerSearchOpen(false);
                    setShowCreateModal(false);
                  }}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Buat Tiket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Ticket Modal */}
      {showEditModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full dark:bg-slate-800">
            <div className="p-5 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Edit Tiket</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTicket(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1 dark:text-slate-400">#{selectedTicket.ticket_number}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Status</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                >
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="PENDING">Pending</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Prioritas</label>
                <select
                  value={editFormData.priority}
                  onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                >
                  <option value="LOW">Low</option>
                  <option value="NORMAL">Normal</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Teknisi</label>
                <select
                  value={editFormData.assigned_to}
                  onChange={(e) => setEditFormData({ ...editFormData, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                >
                  <option value="">Belum ditugaskan</option>
                  {teknisi.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.full_name || t.name || t.username || 'Teknisi'}{t.phone ? ` - ${t.phone}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Teknisi ke-2 (Opsional)</label>
                <select
                  value={editFormData.assigned_to_2}
                  onChange={(e) => setEditFormData({ ...editFormData, assigned_to_2: e.target.value })}
                  data-testid="edit-teknisi-2-select"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                >
                  <option value="">Tidak ada</option>
                  {teknisi.filter(t => String(t.id) !== String(editFormData.assigned_to)).map(t => (
                    <option key={t.id} value={t.id}>
                      {t.full_name || t.name || t.username || 'Teknisi'}{t.phone ? ` - ${t.phone}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Catatan</label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                  placeholder="Tambahkan catatan..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTicket(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                >
                  Batal
                </button>
                <button
                  onClick={handleEditTicket}
                  className="flex-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Simpan Perubahan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 dark:bg-slate-800">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4 dark:bg-red-900/40">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 dark:text-slate-100">Hapus Tiket</h3>
              <p className="text-sm text-gray-500 mb-6 dark:text-slate-400">
                Apakah Anda yakin ingin menghapus tiket <strong>#{selectedTicket.ticket_number}</strong>? 
                Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedTicket(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteTicket}
                  className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;